import {
    useState,
    useEffect,
    ChangeEventHandler,
    KeyboardEventHandler,
} from 'react';
import { checkEmail, checkPassword } from '@utils/dataCheck';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';

const useRegister = (onClose: ICloseEvent['onClose']) => {
    const { user, lang } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        email: '',
        password: '',
        passwordConfirm: '',
        privacyPolicyAccepted: false,
        privacyPolicyError: false,
        toConfrimPage: false,
        emailError: false,
        passwordError: false,
        passwordConfirmError: false,
    };
    const [state, setState] = useState(initState);

    const emailValidate = (email: string) => {
        const isEmailValid = checkEmail(email);
        if (!isEmailValid) {
            setState((prev) => ({
                ...prev,
                emailError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                emailError: false,
            }));
            return true;
        }
    };
    const passwordValidate = (password: string) => {
        const isPasswordValid = checkPassword(password);
        if (!isPasswordValid) {
            setState((prev) => ({
                ...prev,
                passwordError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordError: false,
            }));
            return true;
        }
    };
    const passwordConfirmValidate = (password: string, confirm: string) => {
        if (password !== confirm) {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: true,
            }));
            return false;
        } else {
            setState((prev) => ({
                ...prev,
                passwordConfirmError: false,
            }));
            return true;
        }
    };
    const privacyPolicyValidate = (privacyPolicyAccepted: boolean) => {
        if (privacyPolicyAccepted) {
            return true;
        } else {
            setState((prev) => ({
                ...prev,
                privacyPolicyError: true,
            }));
            return false;
        }
    };
    const handleInputClick = () => {
        user.resetRegisterError();
        user.resetServerError();
        setState((prev) => ({
            ...prev,
            emailError: false,
            passwordError: false,
            passwordConfirmError: false,
            privacyPolicyError: false,
        }));
    };
    const handleEmailChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const email = e.target.value;
        setState((prev) => ({
            ...prev,
            email,
            emailError: false,
            passwordError: false,
            passwordConfirmError: false,
        }));
    };
    const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const password = e.target.value;
        setState((prev) => ({
            ...prev,
            password,
            emailError: false,
            passwordError: false,
            passwordConfirmError: false,
        }));
    };
    const handlePasswordConfirmChange: ChangeEventHandler<HTMLInputElement> = (
        e,
    ) => {
        const passwordConfirm = e.target.value;
        setState((prev) => ({
            ...prev,
            passwordConfirm,
            emailError: false,
            passwordError: false,
            passwordConfirmError: false,
        }));
    };
    const handlePrivacyPolicyClick = () => {
        handleInputClick();
        setState((prev) => ({
            ...prev,
            privacyPolicyAccepted: !prev.privacyPolicyAccepted,
        }));
    };
    const handleSignUpClick = () => {
        const { email, password, passwordConfirm, privacyPolicyAccepted } =
            state;
        const isEmailValid = emailValidate(email);
        const isPasswordValid = passwordValidate(password);
        const isConfirmValid = passwordConfirmValidate(
            password,
            passwordConfirm,
        );
        const isPrivacyPolicyValid = privacyPolicyValidate(
            privacyPolicyAccepted,
        );
        user.resetRegisterError();
        user.resetServerError();
        if (
            isEmailValid &&
            isPasswordValid &&
            isConfirmValid &&
            isPrivacyPolicyValid
        ) {
            setState((prev) => ({
                ...prev,
                toConfrimPage: true,
            }));
            user.register(email, password);
        }
    };
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleSignUpClick();
                break;
        }
    };
    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            handleKey(key);
        }
    };
    const handleBtnKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLInputElement;
            target.click();
        }
    };
    const handlers = {
        handleEmailChange,
        handlePasswordChange,
        handlePasswordConfirmChange,
        handlePrivacyPolicyClick,
        handleSignUpClick,
        handleInputClick,
        handleKeyDown,
        handleBtnKeyDown,
    };

    useEffect(() => {
        user.resetRegisterError();
        user.resetServerError();
        return () => {
            user.resetRegisterError();
            user.resetServerError();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        store: user,
        lang,
        state,
        handlers,
    };
};

export default useRegister;
